import { Box, Stack, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { breakpoints, palette } from '../../../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import AIAssistantMessages from './Messages/AIAssistantMessages';
import AIContextHeadline from '../Layout/AIContextHeadline';
import AIAssistantFooter from '../Footer/AIAssistantFooter';
import AIAssistantDialogContentWithHeadline from '../Layout/AIAssistantContentWithHeadline';
import useScreenSize from '../../../../utils/useScreenSize';
import { AIAssistantTextEditorContainer } from '../TextEditor';
import { DIALOG_WIDTH, TEXT_EDITOR_WIDTH } from '../constants';
import {
  aiAssistantEditorContext,
  AIAssistantEditorMode,
} from '../../../../state/AIAssistantEditorProvider';
import { useSelector } from 'react-redux';
import { getCurrentConversation } from '../../../../reducers/aiAssistant.selectors';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../state/AIAssistantContextProvider';
import AIAssistantChatCollapseButton from './AIAssistantChatCollapseButton';
import useAIAssistantAutoScroll from './useAIAssistantAutoScroll';

const getMainContainerStyles = (
  shouldApplyPageModeStyles: boolean,
  isTextEditorOpen: boolean,
  isXsOrSmScreen: boolean
) => {
  return shouldApplyPageModeStyles ? {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'stretch',
    flex: '1 0 0',
    alignSelf: 'stretch',
    height: '100%',
    margin: '0 auto',
    overflow: 'hidden',
  } : {
    display: 'grid',
    ...(isTextEditorOpen && !isXsOrSmScreen 
      ? { gridTemplateColumns: `${TEXT_EDITOR_WIDTH}px 1fr` }
      : { gridTemplateColumns: '1fr' }
    ),
    overflow: 'hidden',
    height: '100%'
  } as const;
};

const getTextEditorWrapperStyles = (shouldApplyPageModeStyles: boolean) => {
  return shouldApplyPageModeStyles ? {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    alignSelf: 'stretch',
    height: '100%',
  } : {};
};

const getChatContentStyles = (
  shouldApplyPageModeStyles: boolean,
  isTextEditorOpen: boolean,
  isXsOrSmScreen: boolean,
  isChatCollapsed: boolean
) => {
  return shouldApplyPageModeStyles ? {
    display: isChatCollapsed ? 'none' : 'flex',
    width: `${DIALOG_WIDTH.SLIM}px`,
    minWidth: '320px',
    maxWidth: `${breakpoints.mobile}px`,
    flexShrink: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 0,
    borderLeft: `1px solid ${palette.divider}`,
    height: '100%',
    overflow: 'hidden',
  } : {
    ...(isTextEditorOpen && !isXsOrSmScreen ? { gridColumn: '2 / 3' } : { gridColumn: '1 / 2' }),
    overflow: 'hidden',
    height: '100%'
  };
};

const AIAssistantChat = () => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const { isTextEditorOpen, mode } = useContext(aiAssistantEditorContext);
  const { displayMode } = useContext(aiAssistantContext);

  useAIAssistantAutoScroll();

  const contentStyles = {
    position: 'relative',
    padding: 0,
    backgroundColor: palette.background.paperElevation1
  };
  const isXsOrSmScreen = isXsScreen || isSmScreen;
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const isPodcastMode = mode === AIAssistantEditorMode.PODCAST;
  const effectiveCollapsed = isPodcastMode || isChatCollapsed;

  const toggleChatCollapsed = () => {
    setIsChatCollapsed(!isChatCollapsed);
  };
  // On small screens, always render text editor (for SwipeableDrawer to work)
  // On larger screens, only render when open
  const shouldRenderTextEditor = isXsOrSmScreen || isTextEditorOpen;
  const mindItemContext = useSelector(getCurrentConversation)?.data?.context?.mindItem;
  
  const shouldApplyPageModeStyles = isPageMode && isTextEditorOpen && !isXsOrSmScreen;
  const shouldShowDivider = shouldApplyPageModeStyles;
  
  const mainContainerStyles = getMainContainerStyles(shouldApplyPageModeStyles, isTextEditorOpen, isXsOrSmScreen);
  const textEditorWrapperStyles = getTextEditorWrapperStyles(shouldApplyPageModeStyles);
  const chatContentStyles =
    getChatContentStyles(shouldApplyPageModeStyles, isTextEditorOpen, isXsOrSmScreen, effectiveCollapsed);

  return (
    <Stack sx={mainContainerStyles}>
      {shouldRenderTextEditor && (
        shouldApplyPageModeStyles ? (
          <Box sx={{ ...textEditorWrapperStyles, position: 'relative' }}>
            <AIAssistantTextEditorContainer />
            {shouldShowDivider && !isPodcastMode && (
              <AIAssistantChatCollapseButton
                isCollapsed={isChatCollapsed}
                onToggle={toggleChatCollapsed}
              />
            )}
          </Box>
        ) : (
          <AIAssistantTextEditorContainer />
        )
      )}
      <Stack sx={chatContentStyles}>
        <AIAssistantDialogContentWithHeadline
          headline={mindItemContext ? <AIContextHeadline /> : null}
          sx={contentStyles}>
          <AIAssistantMessages />
        </AIAssistantDialogContentWithHeadline>
        <AIAssistantFooter
          caption={(
            <Typography variant='caption' sx={{ color: palette.text.secondary }}>
              {t('Please check the AI results for technical accuracy and pedagogical suitability.')}
            </Typography>
          )} 
        />
      </Stack>
    </Stack>
  );
};

export default AIAssistantChat;
